import { Modal } from '@trackman/web-shared-components';
import { ChallengeCall, ChallengeResult, ChallengeTeam } from 'interfaces/tagging/ApiModels/IChallengeTag';
import React from 'react';
import styled from 'styled-components';
import { brand, darkgrey, grey, white } from 'styles/Colors';

export type { ChallengeCall, ChallengeResult, ChallengeTeam };

export interface ABSModalProps {
  open: boolean;
  isChallenge: boolean;
  onClose: () => void;
  pitchNumber?: number;
  inning: string;
  homeTeamName: string;
  awayTeamName: string;
  challengingTeam: ChallengeTeam | null;
  umpireCall: ChallengeCall | null;
  challengeOutcome: ChallengeResult | null;
  onChallengeTeamChange: (team: ChallengeTeam) => void;
  onUmpireCallChange: (call: ChallengeCall) => void;
  onOutcomeChange: (outcome: ChallengeResult) => void;
  onRemoveFromPlay: () => void;
  onApplyToPlay: () => void;
}

// --- Styled components ---

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const PitchDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BoldText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 18.2px;
  color: ${darkgrey};
  margin: 0;
`;

const RegularText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18.2px;
  color: ${darkgrey};
  margin: 0;
`;

const QuestionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioOptionsRow = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: ${darkgrey};
  white-space: nowrap;
  user-select: none;
`;

const RadioCircle = styled.div<{ $selected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid ${({ $selected }) => ($selected ? brand : grey)};
  background: ${white};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RadioDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${brand};
`;

const HiddenRadioInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

// --- Radio button sub-component ---

interface RadioButtonProps {
  name: string;
  value: string;
  label: string;
  selected: boolean;
  onChange: (value: string) => void;
  'data-testid'?: string;
}

const RadioButton: React.FC<RadioButtonProps> = ({ name, value, label, selected, onChange, 'data-testid': dataTestId }) => (
  <RadioLabel>
    <HiddenRadioInput type='radio' name={name} value={value} checked={selected} onChange={() => onChange(value)} data-testid={dataTestId} />
    <RadioCircle $selected={selected} onClick={() => onChange(value)}>
      {selected && <RadioDot />}
    </RadioCircle>
    {label}
  </RadioLabel>
);

// --- Main component ---

export const ABSModal: React.FC<ABSModalProps> = ({
  open,
  onClose,
  pitchNumber,
  inning,
  homeTeamName,
  awayTeamName,
  challengingTeam,
  umpireCall,
  challengeOutcome,
  onChallengeTeamChange,
  onUmpireCallChange,
  onOutcomeChange,
  onRemoveFromPlay,
  onApplyToPlay,
  isChallenge,
}) => {
  const canApply = challengingTeam !== null && umpireCall !== null && challengeOutcome !== null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Pitch Call Challenged'
      cancelBtn={
        isChallenge
          ? {
              label: 'Remove from play',
              onClick: onRemoveFromPlay,
              style: { backgroundColor: '#F5F5F5', color: '#414141' },
              'data-testid': 'abs-challenge-remove-button',
            }
          : undefined
      }
      confirmBtn={{
        label: 'Apply to play',
        onClick: onApplyToPlay,
        disabled: !canApply,
        'data-testid': 'abs-challenge-apply-button',
      }}
      maxWidth='xs'
      fullWidth
    >
      <BodyContainer>
        <PitchDescription>
          <BoldText>
            Pitch {pitchNumber}, {inning} Inning
          </BoldText>
          <RegularText>Annotate the outcome of the Challenge and apply it to the play.</RegularText>
        </PitchDescription>

        <QuestionGroup>
          <BoldText>Which team Challenged the pitch call?</BoldText>
          <RadioOptionsRow>
            <RadioButton
              name='challengingTeam'
              value='Home'
              label={homeTeamName}
              selected={challengingTeam === 'Home'}
              onChange={(v) => onChallengeTeamChange(v as ChallengeTeam)}
              data-testid='abs-challenge-team-home-radio'
            />
            <RadioButton
              name='challengingTeam'
              value='Away'
              label={awayTeamName}
              selected={challengingTeam === 'Away'}
              onChange={(v) => onChallengeTeamChange(v as ChallengeTeam)}
              data-testid='abs-challenge-team-away-radio'
            />
          </RadioOptionsRow>
        </QuestionGroup>

        <QuestionGroup>
          <BoldText>What was the umpire's call?</BoldText>
          <RadioOptionsRow>
            <RadioButton
              name='umpireCall'
              value='Ball'
              label='Ball'
              selected={umpireCall === 'Ball'}
              onChange={(v) => onUmpireCallChange(v as ChallengeCall)}
              data-testid='abs-challenge-umpire-call-ball-radio'
            />
            <RadioButton
              name='umpireCall'
              value='Strike'
              label='Strike'
              selected={umpireCall === 'Strike'}
              onChange={(v) => onUmpireCallChange(v as ChallengeCall)}
              data-testid='abs-challenge-umpire-call-strike-radio'
            />
          </RadioOptionsRow>
        </QuestionGroup>

        <QuestionGroup>
          <BoldText>What was the outcome of the Challenge?</BoldText>
          <RadioOptionsRow>
            <RadioButton
              name='challengeOutcome'
              value='Confirmed'
              label='Confirmed'
              selected={challengeOutcome === 'Confirmed'}
              onChange={(v) => onOutcomeChange(v as ChallengeResult)}
              data-testid='abs-challenge-outcome-confirmed-radio'
            />
            <RadioButton
              name='challengeOutcome'
              value='Overturned'
              label='Overturned'
              selected={challengeOutcome === 'Overturned'}
              onChange={(v) => onOutcomeChange(v as ChallengeResult)}
              data-testid='abs-challenge-outcome-overturned-radio'
            />
          </RadioOptionsRow>
        </QuestionGroup>
      </BodyContainer>
    </Modal>
  );
};
